/**
 * 1.0.1: YUIChat 项目 - 语言切换组件
 * 1.3.89: 改为简约扁平风格，使用字符徽章代替 emoji 旗帜
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check } from 'lucide-react';

interface Language {
  code: string;
  label: string;
  abbr: string; // 1.3.89: 简约字符标识
}

// 1.3.89: 使用简约字符代替 emoji 旗帜
const languages: Language[] = [
  { code: 'zh', label: '中文', abbr: '中' },
  { code: 'ja', label: '日本語', abbr: 'あ' },
  { code: 'en', label: 'English', abbr: 'A' },
];

// 1.2.28: 添加 direction prop 控制下拉菜单弹出方向
interface LanguageSwitcherProps {
  direction?: 'up' | 'down'; // 下拉菜单方向：up=向上弹出，down=向下弹出
}

export function LanguageSwitcher({ direction = 'up' }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get current language code (handle 'zh-CN' -> 'zh')
  const getCurrentLanguageCode = (): string => {
    const lang = i18n.language || 'zh';
    if (lang.startsWith('zh')) return 'zh';
    if (lang.startsWith('ja')) return 'ja';
    if (lang.startsWith('en')) return 'en';
    return lang;
  };

  const [currentLang, setCurrentLang] = useState<Language>(
    languages.find(lang => lang.code === getCurrentLanguageCode()) || languages[1] // Default to Chinese
  );

  // Update current language when i18n language changes
  useEffect(() => {
    const langCode = getCurrentLanguageCode();
    const lang = languages.find(l => l.code === langCode) || languages[1];
    setCurrentLang(lang);
  }, [i18n.language]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageChange = async (lang: Language) => {
    await i18n.changeLanguage(lang.code);
    setCurrentLang(lang);
    setIsOpen(false);
  };

  // 1.3.89: 简约扁平风格，适配 YUIChat 品牌
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Language Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Switch Language"
        title={t('switchLanguage')}
      >
        {/* 1.3.89: 简约字符徽章 */}
        <span 
          className="inline-flex items-center justify-center w-6 h-6 rounded-md text-xs font-semibold text-white flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)' }}
        >
          {currentLang.abbr}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {/* 1.3.89: 根据 direction prop 控制弹出方向 */}
      {isOpen && (
        <div className={`absolute right-0 w-40 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50 ${
          direction === 'down' ? 'top-full mt-2' : 'bottom-full mb-2'
        }`}>
          {languages.map((lang) => {
            const isSelected = lang.code === currentLang.code;
            return (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors ${
                  isSelected ? 'bg-purple-50' : ''
                }`}
              >
                {/* 1.3.89: 简约字符徽章 */}
                <span 
                  className={`inline-flex items-center justify-center w-6 h-6 rounded-md text-xs font-semibold flex-shrink-0 transition-all ${
                    isSelected 
                      ? 'text-white' 
                      : 'text-purple-600 bg-purple-100'
                  }`}
                  style={isSelected ? { background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)' } : {}}
                >
                  {lang.abbr}
                </span>
                
                {/* Language Label */}
                <span
                  className={`flex-1 text-sm text-left ${
                    isSelected ? 'text-purple-600 font-medium' : 'text-gray-600'
                  }`}
                >
                  {lang.label}
                </span>

                {/* Check Icon - Only show for selected */}
                {isSelected && (
                  <Check className="w-4 h-4 text-purple-600 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

